// Physician Request — the one letter AHFC sends a PCP or specialist to ask for
// GAPP paperwork. A single template with a checklist: the coordinator ticks
// which of the three items they need and the letter is generated from the
// selection — PPOT (Appendix T) for signature, a Letter of Medical Necessity,
// and/or all available medical records. Page 1 doubles as the fax cover.
//
// Same @react-pdf primitives as the signing packets, so the on-screen preview
// and the filed/faxed PDF are the same render. Outbound faxing (Faxage
// `sendfax`) attaches this PDF first, then the pre-filled Appendix T when the
// PPOT item is selected.
import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, View, Text, styles, COLORS, Letterhead } from './pdfPrimitives';
import { COMPANY } from './letterhead';
import { parseLocalDate } from '../dateUtils';
import { fileName } from './fileNames';
import { format } from 'date-fns';

// The three things we ask for. Order here is the order on the letter.
export const REQUEST_ITEMS = [
  {
    key: 'ppot',
    short: 'PPOT',
    label: 'Physician Plan of Treatment (GAPP Appendix T)',
    detail: 'Please complete, sign and date the enclosed GAPP Appendix T — current diagnoses, medications, skilled nursing interventions, and nursing hours per week ordered.',
    detailNotEnclosed: 'Please complete, sign and date the GAPP Physician Plan of Treatment (DCH Appendix T) — current diagnoses, medications, skilled nursing interventions, and nursing hours per week ordered. Call us if your office needs a copy of the form.',
  },
  {
    key: 'lmn',
    short: 'LMN',
    label: 'Letter of Medical Necessity',
    detail: 'On practice letterhead, signed by the physician: the qualifying diagnoses, the skilled interventions required, why care must be delivered in the home, and the recommended nursing hours per week.',
  },
  {
    key: 'records',
    short: 'Medical Records',
    label: 'Medical Records',
    detail: 'All available records for the period below: office visit notes, hospital discharge summaries, specialist consults, current medication list, therapy evaluations (PT/OT/ST), immunizations, and the most recent H&P.',
  },
];

export const RECIPIENT_KINDS = [
  { key: 'pcp',      label: 'Primary Care Physician' },
  { key: 'ordering', label: 'Ordering Physician (Specialist)' },
  { key: 'other',    label: 'Other provider' },
];

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = parseLocalDate(iso);
  return d && !isNaN(d) ? format(d, 'MMMM d, yyyy') : String(iso);
};
const fmtShort = (iso) => {
  if (!iso) return '';
  const d = parseLocalDate(iso);
  return d && !isNaN(d) ? format(d, 'MM/dd/yyyy') : String(iso);
};
const fmtFax = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (raw || '');
};

// Human summary of the selection — used for the letter subject, the file name
// and the Chat/activity message ("PPOT + LMN + Medical Records").
export function requestTitle(items) {
  const picked = REQUEST_ITEMS.filter(i => items?.[i.key]).map(i => i.short);
  return picked.length ? picked.join(' + ') : 'Physician Request';
}

export function requestFileName(data) {
  const last = data.client?.last_name || '';
  const first = data.client?.first_name || '';
  const who = [last, first].filter(Boolean).join(', ');
  return fileName(['Physician Request', requestTitle(data.items).replace(/ \+ /g, ' '), who, data.date || '']);
}

const Row = ({ label, value, wide }) => (
  <View style={{ flexDirection: 'row', marginBottom: 3 }}>
    <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 8, color: COLORS.mid, width: wide ? 70 : 52 }}>{label}</Text>
    <Text style={{ fontSize: 9, color: '#000', flex: 1 }}>{value || '—'}</Text>
  </View>
);

function PhysicianRequestPdf({ data }) {
  const { client = {}, recipient = {}, items = {}, records = {}, hours = {}, sender = {} } = data;
  const hoursLine = [
    hours.skilled ? `${hours.skilled} hrs/wk skilled nursing (RN/LPN)` : null,
    hours.unskilled ? `${hours.unskilled} hrs/wk unskilled care (PSS/CNA)` : null,
  ].filter(Boolean).join(' · ');
  const picked = REQUEST_ITEMS.filter(i => items[i.key]);
  const clientName = [client.last_name, client.first_name].filter(Boolean).join(', ');
  const enclosures = [];
  if (items.ppot && data.ppotEnclosed !== false) enclosures.push(data.ppotPrefilled ? 'GAPP Appendix T — Physician Plan of Treatment, pre-filled for signature' : 'GAPP Appendix T — Physician Plan of Treatment (blank, for completion)');
  if (data.releaseEnclosed) enclosures.push('Authorization for Release of Information signed by the parent/guardian');
  const message = String(data.notes || '').trim();

  return (
    <Document title={`Physician Request – ${requestTitle(items)}`} author={COMPANY.name}>
      <Page size="LETTER" style={[styles.page, { paddingTop: 34, paddingBottom: 50 }]}>
        <View style={{ marginBottom: -8 }}><Letterhead /></View>

        {/* ── Fax cover block ─────────────────────────────────────────────── */}
        <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 13, color: COLORS.navy, marginBottom: 2 }}>
          REQUEST FOR PHYSICIAN DOCUMENTATION
        </Text>
        <Text style={{ fontSize: 8.5, color: COLORS.mid, marginBottom: 8 }}>
          {requestTitle(items)} · Georgia Pediatric Program (GAPP) · Fax cover and request letter
        </Text>

        <View style={{ flexDirection: 'row', borderWidth: 0.5, borderColor: COLORS.border, borderRadius: 3, marginBottom: 8 }}>
          <View style={{ flex: 1, padding: 6, borderRightWidth: 0.5, borderRightColor: COLORS.border }}>
            <Text style={styles.sectionHdr}>TO</Text>
            <Row label="Name" value={recipient.name} />
            <Row label="Practice" value={recipient.practice} />
            <Row label="Fax" value={fmtFax(recipient.fax)} />
            <Row label="Phone" value={recipient.phone} />
          </View>
          <View style={{ flex: 1, padding: 6 }}>
            <Text style={styles.sectionHdr}>FROM</Text>
            <Row label="Sender" value={sender.name ? `${sender.name}${sender.title ? `, ${sender.title}` : ''}` : COMPANY.name} />
            <Row label="Fax" value={COMPANY.fax} />
            <Row label="Phone" value={sender.phone || COMPANY.tel} />
          </View>
        </View>

        <View style={{ backgroundColor: COLORS.grey, padding: 6, marginBottom: 8, borderRadius: 3 }}>
          <View style={{ flexDirection: 'row' }}>
            <View style={{ flex: 1 }}>
              <Row label="Date" value={fmtDate(data.date)} />
              <Row label="Patient" value={clientName} />
              <Row label="DOB" value={fmtShort(client.date_of_birth)} />
            </View>
            <View style={{ flex: 1 }}>
              <Row label="Medicaid ID" value={client.medicaid_id} wide />
              <Row label="AHFC ID" value={client.client_id} wide />
              <Row label="Needed by" value={fmtDate(data.neededBy)} wide />
            </View>
          </View>
        </View>

        {/* ── Letter ──────────────────────────────────────────────────────── */}
        <Text style={styles.body}>Dear {recipient.name ? recipient.name : 'Provider'},</Text>
        <Text style={styles.bodyJust}>
          {COMPANY.name} provides in-home skilled nursing to <Text style={{ fontFamily: 'Helvetica-Bold' }}>{clientName || 'the patient named above'}</Text> under
          the Georgia Pediatric Program (GAPP), administered by the Georgia Department of Community Health. To {data.purpose === 'renew' ? 'continue' : 'establish and continue'} services
          and complete the prior-authorization file, we are requesting the {picked.length === 1 ? 'item' : `${picked.length} items`} listed below from your office.
        </Text>

        <Text style={[styles.formSub, { marginTop: 4, marginBottom: 4 }]}>ITEMS REQUESTED</Text>
        {picked.length === 0 && (
          <Text style={[styles.body, { color: COLORS.mid, fontFamily: 'Helvetica-Oblique' }]}>No items selected.</Text>
        )}
        {picked.map((item, n) => (
          <View key={item.key} style={{ flexDirection: 'row', marginBottom: 5 }}>
            <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9.5, color: COLORS.navy, width: 16 }}>{n + 1}.</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 9.5, color: COLORS.navy, marginBottom: 1 }}>
                {item.label}
              </Text>
              <Text style={[styles.legal, { color: '#222' }]}>
                {item.key === 'ppot' && data.ppotEnclosed === false ? item.detailNotEnclosed : item.detail}
              </Text>
              {item.key === 'records' && (records.from || records.to) && (
                <Text style={[styles.legal, { fontFamily: 'Helvetica-Bold', color: COLORS.navy }]}>
                  Period: {fmtShort(records.from) || 'earliest available'} through {fmtShort(records.to) || 'present'}
                </Text>
              )}
            </View>
          </View>
        ))}

        {hoursLine ? (
          <Text style={[styles.legal, { fontFamily: 'Helvetica-Bold', color: COLORS.navy, marginTop: 2, marginBottom: 4 }]}>
            Hours under consideration: {hoursLine}
          </Text>
        ) : null}

        {message ? (
          <View style={{ marginTop: 4, marginBottom: 2, paddingLeft: 8, borderLeftWidth: 1.5, borderLeftColor: COLORS.border }}>
            {message.split(/\n{2,}|\n/).map((para, i) => (
              <Text key={i} style={[styles.bodyJust, { marginBottom: 3 }]}>{para}</Text>
            ))}
          </View>
        ) : null}

        <Text style={[styles.bodyJust, { marginTop: 4 }]}>
          <Text style={{ fontFamily: 'Helvetica-Bold', color: COLORS.navy }}>To return: </Text>Please fax the completed documents to <Text style={{ fontFamily: 'Helvetica-Bold' }}>{COMPANY.fax}</Text>
          {data.neededBy ? <Text>, by <Text style={{ fontFamily: 'Helvetica-Bold' }}>{fmtDate(data.neededBy)}</Text> if at all possible</Text> : null}.
          Items may be returned separately as each becomes available.
          Questions: {sender.name || COMPANY.name} at {sender.phone || COMPANY.tel} or {COMPANY.email}.
        </Text>

        <Text style={[styles.legal, { marginTop: 4 }]}>
          <Text style={{ fontFamily: 'Helvetica-Bold', color: COLORS.navy }}>Authorization. </Text>This request is made for treatment and care-coordination purposes on behalf of a patient under our care.
          Disclosure of protected health information between covered entities for treatment is permitted under
          45 CFR §164.506 without a separate patient authorization.
          {data.releaseEnclosed
            ? ' A signed Authorization for Release of Information from the parent/guardian is enclosed for your records.'
            : ' If your office requires a signed release from the parent/guardian before responding, please let us know and we will provide one.'}
        </Text>

        {enclosures.length > 0 && (
          <Text style={[styles.legal, { marginTop: 4 }]}>
            <Text style={{ fontFamily: 'Helvetica-Bold', color: COLORS.navy }}>Enclosed: </Text>{enclosures.join('; ')}.
          </Text>
        )}

        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 6 }}>
          <Text style={styles.body}>Thank you for your partnership in this child's care.</Text>
          <View style={{ width: 220 }}>
            <View style={{ borderTopWidth: 0.8, borderTopColor: '#000', marginTop: 10 }} />
            <Text style={{ fontSize: 9, marginTop: 2 }}>{sender.name || ' '}</Text>
            <Text style={{ fontSize: 8, color: COLORS.mid }}>{sender.title || 'Care Coordination'} · {COMPANY.name}</Text>
          </View>
        </View>

        <Text fixed style={{ position: 'absolute', bottom: 16, left: 54, right: 54, fontSize: 6.5, color: COLORS.mid, textAlign: 'justify', lineHeight: 1.35 }}>
          CONFIDENTIALITY NOTICE: This facsimile contains protected health information that is privileged and confidential under HIPAA
          (45 CFR Parts 160 and 164). It is intended only for the recipient named above. If you have received this transmission in error,
          please notify {COMPANY.name} immediately at {COMPANY.tel} and destroy all copies. Any review, disclosure, copying or distribution
          by anyone other than the intended recipient is prohibited.
        </Text>
      </Page>
    </Document>
  );
}

export async function renderPhysicianRequestBlob(data) {
  return pdf(<PhysicianRequestPdf data={data} />).toBlob();
}

export { blobToBase64 } from './trainingCertificatePdf';
